<template>
  <div class="deposit-detail-container">
    <SidebarNav @update:sidebarNavCollapsed="sidebarNavCollapsed = $event" />
    <SearchBar :sidebar-nav-collapsed="sidebarNavCollapsed" />
    <div
      class="deposit-detail-content"
      :class="{['deposit-detail-content-sidebar-collapse']: sidebarNavCollapsed}"
    >
      <div class="deposit-detail-content-sub-container">
        <div class="options-bar">
          <div class="back-to-deposits" @click="() => navigateTo('deposits')" :id="getElementId('go_back')">
            <img src="@/app/assets/images/back.svg" alt="Back to deposits icon" />
            <span>{{ $t('deposits.actions.backToDeposits') }}</span>
          </div>
        </div>

        <div class="error" v-if="deposit && failureReason">
          <img src="@/app/assets/images/red-exclamation.svg" alt="Error" />
          {{ failureReason }}
        </div>

        <div class="loading-message" v-if="!deposit">
          Fetching deposit...
        </div>
        <div class="main-layout-container" v-else>
          <div class="cl-dock__main">
            <div class="layout-container">
              <div class="cards">
                <div class="card-container first-card">
                  <div class="card">
                    <DepositInfo :deposit="deposit" />
                  </div>
                </div>

                <div class="card-container" id="overview">
                  <div class="card">
                    <DepositOverview :deposit="deposit" />
                  </div>
                </div>
                <div class="card-container" id="breakdown">
                  <div class="card">
                    <DepositBreakdown :deposit="deposit" v-if="deposit.breakdown" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="cl-dock__sidebar">
            <TableOfContents :sections="sections" />
          </div>
        </div>
        <PortalFooter />
      </div>
    </div>
  </div>
</template>

<script>
import SidebarNav from "@/components/SidebarNav";
import {portalCheckMixin} from "@/mixin/portalPageCheck";
import {navigateTo} from "@/router";
import {mapGetters} from "vuex";
import SearchBar from "@/components/SearchBar";
import PortalFooter from "@/components/portal/PortalFooter.vue";
import {PendoPrefixes} from "@/app/utils/common/constants";
import TableOfContents from "@/components/common/TableOfContents.vue";
import locales from "@/i18n/locales";
import deposit from '@/api/deposits';
import DepositInfo from '@/components/deposits/DepositInfo.vue';
import DepositOverview from '@/components/deposits/DepositOverview.vue';
import DepositBreakdown from '@/components/deposits/DepositBreakdown.vue';
import {resolvePayoutFailureMessage} from "@/app/utils/common/functions/deposit-util";

export default {
  name: "DepositDetailsViewNew",
  components: {
    DepositBreakdown,
    DepositOverview,
    DepositInfo,
    TableOfContents,
    PortalFooter,
    SearchBar,
    SidebarNav,
  },
  mixins: [portalCheckMixin],
  data: function () {
    return {
      sidebarNavCollapsed: false,
      deposit: null,
      sections: [],
      titles: [
        "profile",
        "address",
      ],
    }
  },
  computed: {
    ...mapGetters('app', ['getMerchantId']),
    loading() {
      return this.deposit === null
    },
    failureReason() {
      if (!this.deposit) {
        return null;
      }
      if (this.deposit.status !== 'FAILED' && this.deposit.status !== 'CANCELED') {
        return null;
      }
      return resolvePayoutFailureMessage(this, this.deposit);
    },
  },
  mounted() {
    this.portalChecks().then(() => {
      this.initialize();
    });
  },
  methods: {
    async initialize() {
      this.deposit = await deposit.fetchDepositById(
        this.getMerchantId,
        this.$route.params.depositId)
      this.sections = [
        {id: 'overview', title: locales.en.tableOfContent.overview, show: true},
        {id: 'breakdown', title: locales.en.tableOfContent.deposits.breakdown, show: true},
      ];
    },
    navigateTo: function (route) {
      navigateTo(route, false, this.$route.query);
    },
    getElementId(id) {
      return PendoPrefixes.CBpay+ this.$route.name + '-' + id;
    }
  },
}
</script>

<style lang="scss">
.deposit-detail-container {
  background: $neutral_50;
  height: 100%;
  overflow: scroll;


  & .loading-message {
    color: $primary_text_dark;
    margin-top: 52px;
  }

  & .error {
    background-color: $danger_50;
    color: $neutral_800;
    padding: 6px 8px;
    width: calc(75% - 16px);
    display: flex;
    gap: 10px;
    align-items: center;
    border: 0.5px solid $danger_100;
    border-radius: 6px;
    margin-bottom: 24px;
    text-align: left;
  }

  & .main-layout-container {
    display: flex;
    justify-content: space-between;
    width: 100%;

    & .cl-dock__main {
      width: calc(75% - 8px);
      margin-right: 32px;

      @media screen and (max-width: 700px) {
        width: 100%;
      }
    }

    & .cl-dock__sidebar {
      top: 50px;
      position: sticky;
      border-radius: 8px;
      width: calc(25% - 12px);
      align-self: flex-start;

      @media screen and (max-width: 700px) {
        display: none;
        width: 0;
      }
    }

    & .layout-container {
      width: 100%;
      overflow: auto;

      & .cards {
        width: 100%;

        & .card-container {
          scroll-margin-top: 70px;
          background-color: #FFFFFF;
          padding: 24px;
          border-radius: 6px;
          margin-top: 24px;

          & .card {
            width: 100%;
          }
        }

        & .first-card {
          margin-top: 0;
        }
      }

    }
  }

  & .deposit-detail-content {
    left: 256px;
    top: 0;
    position: relative;
    width: calc(100% - 256px);
    padding: 24px 32px;
    transition: all 0.2s ease-in;
    justify-content: center;
    display: flex;

    & .deposit-detail-content-sub-container {
      max-width: 1360px;
      width: 100%;
    }

    & .options-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 28px;

      & .back-to-deposits {
        cursor: pointer;
        color: $neutral_600;
        font-weight: $weight_medium;

        & span {
          margin-left: 8px;
        }
      }

      & .respond-buttons {
        display: flex;
        align-items: center;

        & button {
          margin-left: 16px;
          margin-bottom: 0;

          & .c-button__label {
            padding: 0;
          }
        }
      }
    }
  }

  & .deposit-detail-content-sidebar-collapse {
    left: 86px;
    width: calc(100% - 86px);
  }
}
</style>